import React, { useState, useEffect } from 'react';
import axios from '../../config/axios';
import { Link } from 'react-router-dom';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    products: 0,
    bookings: 0,
    testRides: 0,
    users: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      // Fetch all data in parallel
      const [productsRes, bookingsRes, testRidesRes, usersRes] = await Promise.all([
        axios.get('/admin/products', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('/admin/bookings', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('/admin/test-rides', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('/admin/users', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      // Handle different response structures
      const products = productsRes.data?.data || productsRes.data || [];
      const bookings = bookingsRes.data?.data || bookingsRes.data || [];
      const testRides = testRidesRes.data?.data || testRidesRes.data || [];
      const users = usersRes.data?.data || usersRes.data || [];
      
      // Ensure we're working with arrays
      const productsArray = Array.isArray(products) ? products : [];
      const bookingsArray = Array.isArray(bookings) ? bookings : [];
      const testRidesArray = Array.isArray(testRides) ? testRides : [];
      const usersArray = Array.isArray(users) ? users : [];
      
      console.log('Dashboard data:', {
        productsCount: productsArray.length,
        bookingsCount: bookingsArray.length,
        testRidesCount: testRidesArray.length,
        usersCount: usersArray.length
      });
      
      setStats({
        products: productsArray.length,
        bookings: bookingsArray.length,
        testRides: testRidesArray.length,
        users: usersArray.length
      });
      
      // Get recent bookings (last 5)
      setRecentBookings(bookingsArray.slice(0, 5));
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 401) {
        setError('Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/admin/login';
        }, 2000);
      } else {
        setError(error.response?.data?.message || 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Products', value: stats.products, icon: '🛵', color: 'bg-blue-500', link: '/admin/products' },
    { title: 'Total Bookings', value: stats.bookings, icon: '📅', color: 'bg-green-500', link: '/admin/bookings' },
    { title: 'Test Rides', value: stats.testRides, icon: '🚗', color: 'bg-yellow-500', link: '/admin/test-rides' },
    { title: 'Total Users', value: stats.users, icon: '👥', color: 'bg-purple-500', link: '/admin/users' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={fetchDashboardData}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">Dashboard Overview</h1>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Link to={stat.link} key={index}>
            <div className={`${stat.color} text-white rounded-lg shadow-lg p-6 hover:opacity-90 transition transform hover:scale-105`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm opacity-90">{stat.title}</p>
                  <p className="text-3xl font-bold mt-2">{stat.value}</p>
                </div>
                <div className="text-4xl">{stat.icon}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
      
      {/* Recent Bookings */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Recent Bookings</h2>
          <Link to="/admin/bookings" className="text-red-600 hover:underline text-sm">
            View All →
          </Link>
        </div>
        
        {recentBookings.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No bookings found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Booking ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-mono">
                      {booking.booking_number || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {booking.customer_details?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {booking.product?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      ₹{booking.amount || 0}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {booking.status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;